import { element } from "./dom.js";
import {
  blackInk,
  type GlyphLibrary,
  type SquareContent,
  traceInks,
} from "./worksheet-drawing.js";
import type { SheetEntry } from "./worksheet-entries.js";
import type { CharacterFacts } from "./worksheet-facts.js";
import {
  headHeight,
  type KeySize,
  keySize,
  keySizes,
  packRows,
  paginate,
  practiceBlockHeight,
  practiceFor,
  recallBlockHeight,
  type SheetMetrics,
  sheetMetrics,
  titleHeight,
} from "./worksheet-layout.js";
import {
  drawPages,
  type SheetLabels,
  type SheetPart,
  sized,
} from "./worksheet-page.js";
import type { WorksheetGrid, WorksheetState } from "./worksheet-state.js";

export type { SheetLabels } from "./worksheet-page.js";

/**
 * A character writing sheet, as pages ready to print.
 *
 * A sheet has up to three parts, each starting on a page of its own and each
 * asked for separately. The writing practice gives every character a line
 * naming it, its strokes in order, and its rows of squares: the model in
 * black, a few to trace, and the rest empty. The recall test gives each
 * character's reading and meaning over a row of empty squares. The stroke
 * order reference comes last and draws every character large, with an arrow
 * and a number on each stroke, which makes it the recall test's answers too.
 */

/** One character on the sheet, with what the site knows about it. */
export interface WritingItem {
  readonly character: string;

  /** Its own 词典 entry. */
  readonly entry: SheetEntry | undefined;

  /** The entry of the word that brought it, and where it sits in that word. */
  readonly word:
    | { readonly entry: SheetEntry; readonly at: number }
    | undefined;

  /** Its 结构 and 部首, where the site has a record of it. */
  readonly facts?: CharacterFacts | undefined;
}

/**
 * A character's 结构 and 部首, as a line of the sheet prints them, such as
 * `⿰ 左右结构 · 部首 女`. A 独体字 has no description character, and a
 * character with no recorded structure prints its 部首 alone.
 */
export function factsShown(facts: CharacterFacts | undefined): string {
  if (facts === undefined) {
    return "";
  }
  const structure =
    facts.structure === undefined
      ? []
      : [`${facts.structure.symbol} ${facts.structure.name}`.trim()];

  return [...structure, `部首 ${facts.radical}`].join(" · ");
}

/**
 * Where a character's 词典 entry is, for the links on a sheet.
 *
 * A sheet is printed, and it is also saved as a PDF and passed around, and a
 * PDF keeps its links. Each character's line and its stroke order square link
 * to its entry, which is one press from a PDF to the reading, the senses and
 * the animated stroke order.
 */
export function entryAddress(origin: string, character: string): string {
  return `${origin}/cidian/${encodeURIComponent(character)}/`;
}

/** What drawing a sheet needs. */
export interface WritingSheetProperties {
  readonly library: GlyphLibrary;
}

/**
 * The word a character came from, with that character left out on a recall
 * test so the word is a clue rather than the answer.
 */
function wordShown(item: WritingItem, recall: boolean): string {
  if (item.word === undefined) {
    return "";
  }
  const letters = Array.from(item.word.entry.word);

  return recall
    ? letters
        .map((letter, at) => (at === item.word!.at ? "＿" : letter))
        .join("")
    : item.word.entry.word;
}

/**
 * A character's readings, the one its word uses first.
 *
 * A 多音字's entry lists every reading, `xíng, háng, héng` for 行. When the
 * character came from a word whose entry marks its syllables, the reading
 * that word uses leads and the rest follow as "also", so 行 from 银行 reads
 * háng. The word's own entry is the authority, which is the check every
 * other generator leaves to the teacher.
 *
 * The entry's first sense belongs to its first reading, "to go" to xíng, so
 * it is printed only beside that reading. 行 from 银行 is háng with no sense
 * of its own, and the word beside it says what it means there.
 */
export function readingsShown(item: WritingItem): {
  readonly said: string;
  readonly also: string;
  readonly meaning: string;
} {
  const meaning = shortMeaning(item.entry?.meaning ?? "");
  const pinyin = item.entry?.pinyin ?? "";
  const readings = pinyin.split(/,\s*/u).filter((reading) => reading !== "");
  const inWord = item.word?.entry.syllables[item.word.at]?.toLowerCase();

  if (
    inWord === undefined ||
    readings.length < 2 ||
    !readings.includes(inWord)
  ) {
    return { said: pinyin, also: "", meaning };
  }

  return {
    said: inWord,
    also: readings.filter((reading) => reading !== inWord).join(", "),
    meaning: inWord === readings[0] ? meaning : "",
  };
}

/**
 * A sense short enough for the line it is printed on.
 *
 * CC-CEDICT writes cross-references as `您[nin2]` and asides in brackets,
 * which a sheet has no room for and a learner has no use for here. "you
 * (informal, as opposed to courteous 您[nin2])" prints as "you".
 */
export function shortMeaning(meaning: string): string {
  const short = meaning
    .replace(/\s*\([^)]*\)/gu, "")
    .replace(/\[[^\]]*\]/gu, "")
    .trim();

  return short === "" ? meaning : short;
}

/** Draws character writing sheets. */
export class WritingSheet {
  readonly #library: GlyphLibrary;

  constructor(properties: WritingSheetProperties) {
    this.#library = properties.library;
  }

  /**
   * Every page of the sheet, the practice, the recall test and the stroke
   * order reference in that order, leaving out the parts not asked for.
   * @param items - The characters, in the order they are printed. Each must
   * already be held by the library.
   */
  pages(
    state: WorksheetState,
    items: readonly WritingItem[],
    labels: SheetLabels,
  ): readonly HTMLElement[] {
    const counts = items.map((item) =>
      this.#library.strokeCount(item.character),
    );
    const metrics = sheetMetrics(state.paper, counts, state.cell);
    const grid = state.grid;
    const parts: SheetPart[] = [];

    if (state.practice) {
      parts.push({
        title: labels.title,
        pages: this.#paged(
          metrics,
          items.map((item) =>
            this.#practiceBlock(metrics, state, item, labels.origin),
          ),
          counts.map((count) =>
            practiceBlockHeight(metrics, count, practiceFor(count, state).rows),
          ),
        ),
      });
    }
    if (state.recall) {
      parts.push({
        title: `Recall test: ${labels.title}`,
        pages: this.#paged(
          metrics,
          items.map((item) => this.#recallBlock(metrics, item, grid)),
          items.map(() => recallBlockHeight(metrics)),
        ),
      });
    }
    if (state.key) {
      parts.push({
        title: `Stroke order: ${labels.title}`,
        pages: this.#keyPages(metrics, items, counts, labels.origin, grid),
      });
    }

    return drawPages(state.paper, parts, labels);
  }

  /** Deal one part's blocks onto pages, leaving room for its heading. */
  #paged(
    metrics: SheetMetrics,
    blocks: readonly HTMLElement[],
    heights: readonly number[],
  ): (readonly Node[])[] {
    return paginate(heights, {
      first: metrics.height - titleHeight,
      rest: metrics.height,
    }).map((page) => page.map((at) => blocks[at]!));
  }

  #keyPages(
    metrics: SheetMetrics,
    items: readonly WritingItem[],
    counts: readonly (number | undefined)[],
    origin: string,
    grid: WorksheetGrid,
  ): (readonly Node[])[] {
    const sizes = counts.map(keySize);
    const packed = packRows(
      sizes.map((size) => size.width),
      metrics.width,
    );
    // A row holding a large square takes the large size for all of it. The
    // width is there anyway, and a row of one size reads as a row.
    const rowSizes = packed.map((row) =>
      row.some((at) => sizes[at] === keySizes.complex)
        ? keySizes.complex
        : keySizes.simple,
    );
    const rows = packed.map((row, at) =>
      element("div", {
        className: "cb-ws-key-row",
        children: row.map((item) =>
          this.#keyItem(items[item]!, rowSizes[at]!, origin, grid),
        ),
      }),
    );

    return this.#paged(
      metrics,
      rows,
      rowSizes.map((size) => size.height),
    );
  }

  #head(item: WritingItem, origin: string, recall: boolean): HTMLElement {
    const children: Node[] = [];
    if (!recall) {
      children.push(
        element("span", {
          className: "cb-ws-head-hanzi",
          text: item.character,
          attributes: { lang: "zh-Hans" },
        }),
      );
    }
    if (item.entry !== undefined) {
      const { said, also, meaning } = readingsShown(item);
      children.push(
        element("span", {
          className: "cb-ws-head-pinyin",
          text: said,
          attributes: { lang: "zh-Latn-CN-pinyin" },
        }),
      );
      if (also !== "") {
        children.push(
          element("span", {
            className: "cb-ws-head-also",
            text: `also ${also}`,
            attributes: { lang: "zh-Latn-CN-pinyin" },
          }),
        );
      }
      if (meaning !== "") {
        children.push(
          element("span", { className: "cb-ws-head-meaning", text: meaning }),
        );
      }
    }
    const count = this.#library.strokeCount(item.character);
    if (count !== undefined && !recall) {
      children.push(
        element("span", {
          className: "cb-ws-head-count",
          text: `${String(count)} ${count === 1 ? "stroke" : "strokes"}`,
        }),
      );
    }
    if (item.word !== undefined) {
      children.push(
        element("span", {
          className: "cb-ws-head-word",
          children: [
            element("span", { text: "in " }),
            element("span", {
              text: wordShown(item, recall),
              attributes: { lang: "zh-Hans" },
            }),
            element("span", {
              text: ` ${item.word.entry.pinyin}`,
              attributes: { lang: "zh-Latn-CN-pinyin" },
            }),
            element("span", { text: `, ${item.word.entry.meaning}` }),
          ],
        }),
      );
    }

    const facts = factsShown(item.facts);
    if (facts !== "" && !recall) {
      children.push(
        element("span", {
          className: "cb-ws-head-facts cb-ws-facts",
          text: facts,
          attributes: { lang: "zh-Hans" },
        }),
      );
    }

    // No link on a recall test, whose link would name the answer.
    const head = recall
      ? element("p", { className: "cb-ws-head", children })
      : element("a", {
          className: "cb-ws-head",
          attributes: { href: entryAddress(origin, item.character) },
          children,
        });

    return sized(head, {
      height: headHeight,
    });
  }

  #practiceBlock(
    metrics: SheetMetrics,
    state: WorksheetState,
    item: WritingItem,
    origin: string,
  ): HTMLElement {
    const children: Node[] = [this.#head(item, origin, false)];
    const { character } = item;
    const count = this.#library.strokeCount(character);

    if (count !== undefined) {
      const order = element("div", { className: "cb-ws-order" });
      for (let at = 0; at < count; at += metrics.strokesAcross) {
        const squares: SquareContent[] = [];
        for (
          let stroke = at;
          stroke < Math.min(count, at + metrics.strokesAcross);
          stroke += 1
        ) {
          squares.push({ kind: "stroke", character, stroke });
        }
        order.append(
          this.#library.row(
            metrics.strokeCell,
            squares,
            at === 0 ? `The strokes of ${character} in order` : undefined,
            "fine",
            state.grid,
          ),
        );
      }
      children.push(order);
    }

    const across = metrics.cellsAcross;
    const { trace, rows } = practiceFor(count, state);
    const tracing = Math.min(trace, across - 1);
    const ink = traceInks[state.ink];
    for (let row = 0; row < rows; row += 1) {
      const squares: SquareContent[] = Array.from(
        { length: across },
        (_, at) => {
          if (row > 0) {
            return { kind: "empty" };
          }
          if (at === 0) {
            return { kind: "glyph", character, ink: blackInk };
          }

          return at <= tracing
            ? { kind: "glyph", character, ink }
            : { kind: "empty" };
        },
      );
      children.push(
        this.#library.row(
          metrics.cell,
          squares,
          undefined,
          "practice",
          state.grid,
        ),
      );
    }

    return sized(element("div", { className: "cb-ws-block", children }), {
      height: practiceBlockHeight(metrics, count, rows),
    });
  }

  #recallBlock(
    metrics: SheetMetrics,
    item: WritingItem,
    grid: WorksheetGrid,
  ): HTMLElement {
    const squares: SquareContent[] = Array.from(
      { length: metrics.cellsAcross },
      () => ({ kind: "empty" }),
    );

    return sized(
      element("div", {
        className: "cb-ws-block",
        children: [
          this.#head(item, "", true),
          this.#library.row(metrics.cell, squares, undefined, "practice", grid),
        ],
      }),
      { height: recallBlockHeight(metrics) },
    );
  }

  #keyItem(
    item: WritingItem,
    size: KeySize,
    origin: string,
    grid: WorksheetGrid,
  ): HTMLElement {
    const count = this.#library.strokeCount(item.character);

    const shown = element("a", {
      className: "cb-ws-key-item",
      attributes: { href: entryAddress(origin, item.character) },
      children: [
        this.#library.row(
          size.glyph,
          [{ kind: "numbered", character: item.character }],
          `${item.character} with its strokes numbered`,
          "practice",
          grid,
        ),
        element("span", {
          className: "cb-ws-key-pinyin",
          text: item.entry?.pinyin ?? "",
          attributes: { lang: "zh-Latn-CN-pinyin" },
        }),
        element("span", {
          className: "cb-ws-key-count",
          // The facts share the stroke count's line, since the box has room
          // for two lines under the square.
          children: this.#keyLine(count, factsShown(item.facts)),
        }),
      ],
    });

    return sized(shown, { width: size.width, height: size.height });
  }

  #keyLine(count: number | undefined, facts: string): readonly Node[] {
    const strokes =
      count === undefined
        ? ""
        : `${String(count)} ${count === 1 ? "stroke" : "strokes"}`;
    if (facts === "") {
      return [document.createTextNode(strokes)];
    }

    return [
      document.createTextNode(strokes === "" ? "" : `${strokes} · `),
      element("span", {
        className: "cb-ws-facts",
        text: facts,
        attributes: { lang: "zh-Hans" },
      }),
    ];
  }
}
